import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  ONBOARDING_BODY_SX,
  ONBOARDING_PRIMARY_BUTTON_SX,
  ONBOARDING_SMALL_TEXT_BUTTON,
} from "../../features/organisations/onboarding/onboardingDesign.js";
import { CodeOrScanCard } from "./CodeOrScanCard.js";
import type { DeviceSignInState } from "./useDeviceSignIn.js";

interface Props {
  state: DeviceSignInState;
  onRestart: () => void;
  onBack: () => void;
}

function useNow(active: boolean): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [active]);
  return now;
}

/** The body of the "sign in with your phone" view (heading and mark live on the page). */
export const DeviceSignInView = ({ state, onRestart, onBack }: Props) => {
  const { t } = useTranslate(["Global"]);
  const now = useNow(state.status === "pending");

  const body = (text: string) => (
    <Typography
      sx={{
        ...ONBOARDING_BODY_SX,
        mt: "11px",
        width: "100%",
        textWrap: "balance",
      }}
    >
      {text}
    </Typography>
  );

  const action = (label: string, onClick: () => void) => (
    <Button
      fullWidth
      variant="contained"
      disableElevation
      onClick={onClick}
      sx={{ ...ONBOARDING_PRIMARY_BUTTON_SX, mt: "28px" }}
    >
      {label}
    </Button>
  );

  const backButton = (
    <Button
      variant="text"
      onClick={onBack}
      sx={{ ...ONBOARDING_SMALL_TEXT_BUTTON, mt: "16px" }}
    >
      {t("Back", { ns: "Global" })}
    </Button>
  );

  if (state.status === "pending") {
    const minutes = Math.max(1, Math.ceil((state.expiresAt - now) / 60_000));
    return (
      <Stack sx={{ width: "100%", alignItems: "center" }}>
        {body(
          t(
            "On the phone you registered on, open {verificationUri} and confirm the sign-in there.",
            { ns: "Global", verificationUri: state.verificationUri }
          )
        )}
        <Box sx={{ width: "100%", mt: "32px" }}>
          <CodeOrScanCard
            code={state.userCode}
            qrValue={state.verificationUriComplete}
            data-testid="device-sign-in-code"
          />
        </Box>
        <Typography sx={{ ...ONBOARDING_BODY_SX, mt: "24px" }}>
          {t("The code is valid for {minutes} more minutes.", {
            ns: "Global",
            minutes,
          })}
        </Typography>
        {backButton}
      </Stack>
    );
  }

  if (state.status === "success") {
    return body(t("Signing in…", { ns: "Global" }));
  }

  if (
    state.status === "expired" ||
    state.status === "denied" ||
    state.status === "error"
  ) {
    return (
      <Stack sx={{ width: "100%", maxWidth: 500, alignItems: "center" }}>
        {body(
          state.status === "expired"
            ? t("The code has expired.", { ns: "Global" })
            : state.status === "denied"
              ? t("The sign-in was declined on the phone.", { ns: "Global" })
              : t("Something went wrong. Please try again.", { ns: "Global" })
        )}
        {action(
          state.status === "error"
            ? t("Retry", { ns: "Global" })
            : t("Show a new code", { ns: "Global" }),
          onRestart
        )}
        {backButton}
      </Stack>
    );
  }

  // idle / requesting: the code is on its way.
  return (
    <Stack sx={{ alignItems: "center", mt: "32px" }}>
      <CircularProgress aria-label={t("Loading…", { ns: "Global" })} />
      {backButton}
    </Stack>
  );
};
